/**
 * AircraftStep - Step 0 (first step) of the Add Plane wizard
 *
 * Allows users to:
 * - Search by tail number across all org aircraft
 *   - If found: show aircraft info card with "Place on Map" shortcut
 *   - If not found: show "New aircraft" form with type selector
 * - Check "I don't have the tail number" to skip tail and just pick type
 * - Choose "Continue Setup" (full path) or "Place on Map" (quick path)
 */

import { useState, useMemo, useCallback } from 'react'
import { Check, ChevronsUpDown, Loader2, Plane, Search, Plus, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'

import { useAircraftTypes } from '@/hooks/useAircraftTypes'
import { useAircraftList } from '@/hooks/useAircraft'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import { getAircraftLoaderInstance } from '@/services/3d'
import { inferFuelTypeFromCategory } from '@/types/aircraft-types'

interface AircraftStepProps {
  onQuickPlace?: () => void
}

export function AircraftStep({ onQuickPlace }: AircraftStepProps) {
  const {
    selectedExistingAircraftId,
    isCreatingNewAircraft,
    selectedAircraftTypeId,
    selectedAircraftTypeName,
    tailNumber,
    noTailNumber,
    setSelectedExistingAircraft,
    toggleCreateNewAircraft,
    setAircraftType,
    setTailNumber,
    setNoTailNumber,
    activateQuickPlace,
    isStepComplete,
  } = useAddPlaneWizardStore()

  const [typeSearchOpen, setTypeSearchOpen] = useState(false)
  const [typeSearchQuery, setTypeSearchQuery] = useState('')
  const [tailSearchQuery, setTailSearchQuery] = useState(tailNumber || '')

  // Fetch aircraft types (for new aircraft)
  const { data: aircraftTypes, isLoading: isLoadingTypes } = useAircraftTypes()

  // Fetch all org aircraft for tail number search (no owner filter)
  const { data: allAircraft, isLoading: isLoadingAircraft } = useAircraftList()

  // Search results: filter org aircraft by tail number as user types
  const tailSearchResults = useMemo(() => {
    if (!allAircraft || !tailSearchQuery || tailSearchQuery.length < 1) return []
    const query = tailSearchQuery.toUpperCase()
    return allAircraft
      .filter((aircraft) =>
        aircraft.tail_number?.toUpperCase().includes(query)
      )
      .slice(0, 5)
  }, [allAircraft, tailSearchQuery])

  // Check if typed tail exactly matches an existing aircraft
  const exactMatch = useMemo(() => {
    if (!allAircraft || !tailSearchQuery) return null
    const query = tailSearchQuery.toUpperCase().trim()
    return allAircraft.find(
      (a) => a.tail_number?.toUpperCase() === query
    ) ?? null
  }, [allAircraft, tailSearchQuery])

  // Get selected existing aircraft details
  const selectedAircraft = useMemo(() => {
    if (!selectedExistingAircraftId || !allAircraft) return null
    return allAircraft.find((a) => a.id === selectedExistingAircraftId) ?? null
  }, [allAircraft, selectedExistingAircraftId])

  // Get display name for existing aircraft
  const getAircraftDisplayName = (aircraft: {
    manufacturer?: string | null
    model?: string | null
    aircraft_type_manufacturer?: string | null
    aircraft_type_model?: string | null
  }) => {
    // Prefer manual overrides, fall back to joined aircraft_types data
    const manual = `${aircraft.manufacturer || ''} ${aircraft.model || ''}`.trim()
    if (manual) return manual
    const fromType = `${aircraft.aircraft_type_manufacturer || ''} ${aircraft.aircraft_type_model || ''}`.trim()
    return fromType || 'Unknown Type'
  }

  // Handle tail number input change
  const handleTailSearchChange = useCallback((value: string) => {
    const upper = value.toUpperCase()
    setTailSearchQuery(upper)
    setTailNumber(upper)

    // If exact match found, auto-select it
    if (allAircraft) {
      const match = allAircraft.find(
        (a) => a.tail_number?.toUpperCase() === upper.trim()
      )
      if (match) {
        const fuelType = (match.aircraft_type_default_fuel_type as 'jet_a' | 'avgas' | null)
          ?? inferFuelTypeFromCategory(match.aircraft_type_category)
        setSelectedExistingAircraft(
          match.id,
          match.tail_number,
          null,
          getAircraftDisplayName(match),
          match.owner_client_id,
          fuelType
        )
      } else {
        // No match — clear existing selection & create mode so autocomplete shows
        if (selectedExistingAircraftId) {
          setSelectedExistingAircraft(null, '', null, null, null, null)
        }
        if (isCreatingNewAircraft) {
          // Set directly to avoid toggleCreateNewAircraft side effects
          useAddPlaneWizardStore.setState({ isCreatingNewAircraft: false })
        }
      }
    }
  }, [allAircraft, setTailNumber, setSelectedExistingAircraft, selectedExistingAircraftId, isCreatingNewAircraft])

  // Handle selecting from search results
  const handleExistingAircraftSelect = useCallback((aircraftId: string) => {
    const aircraft = allAircraft?.find((a) => a.id === aircraftId)
    if (aircraft) {
      const fuelType = (aircraft.aircraft_type_default_fuel_type as 'jet_a' | 'avgas' | null)
        ?? inferFuelTypeFromCategory(aircraft.aircraft_type_category)
      setSelectedExistingAircraft(
        aircraftId,
        aircraft.tail_number,
        null,
        getAircraftDisplayName(aircraft),
        aircraft.owner_client_id,
        fuelType
      )
      setTailSearchQuery(aircraft.tail_number || '')
    }
  }, [allAircraft, setSelectedExistingAircraft])

  // Filter aircraft types based on search query
  const filteredTypes = useMemo(() => {
    if (!aircraftTypes) return []
    if (!typeSearchQuery) return aircraftTypes.slice(0, 50)

    const query = typeSearchQuery.toLowerCase()
    return aircraftTypes
      .filter((type) => {
        const fullName = `${type.manufacturer || ''} ${type.model || ''} ${type.variant || ''}`.toLowerCase()
        return (
          fullName.includes(query) ||
          type.type_code?.toLowerCase().includes(query) ||
          type.icao_designator?.toLowerCase().includes(query)
        )
      })
      .slice(0, 50)
  }, [aircraftTypes, typeSearchQuery])

  // Get selected aircraft type details
  const selectedType = useMemo(() => {
    if (!selectedAircraftTypeId || !aircraftTypes) return null
    return aircraftTypes.find((t) => t.id === selectedAircraftTypeId) ?? null
  }, [aircraftTypes, selectedAircraftTypeId])

  // Get display name for aircraft type
  const getTypeDisplayName = (type: NonNullable<typeof selectedType>) => {
    return `${type.manufacturer || ''} ${type.model || ''}${type.variant ? ` ${type.variant}` : ''}`.trim()
  }

  // Handle aircraft type selection
  const handleTypeSelect = useCallback((typeId: string) => {
    const type = aircraftTypes?.find((t) => t.id === typeId)
    if (type) {
      const fuelType = type.default_fuel_type ?? inferFuelTypeFromCategory(type.category)
      setAircraftType(typeId, getTypeDisplayName(type), null, fuelType)

      // Preload GLTF model for faster placement mode
      if (type.model_url) {
        const loader = getAircraftLoaderInstance()
        loader.preloadModel(type.type_code, type.model_url).catch(() => {
          // Preload failure is non-blocking
        })
      }
    }
    setTypeSearchOpen(false)
    setTypeSearchQuery('')
  }, [aircraftTypes, setAircraftType])

  // Handle "I don't have the tail number" toggle
  const handleNoTailToggle = useCallback((checked: boolean) => {
    setNoTailNumber(checked)
    if (checked) {
      setTailSearchQuery('')
      // Ensure we're in new aircraft mode
      toggleCreateNewAircraft(true)
    }
  }, [setNoTailNumber, toggleCreateNewAircraft])

  // Handle quick place button
  const handleQuickPlace = useCallback(() => {
    activateQuickPlace()
    onQuickPlace?.()
  }, [activateQuickPlace, onQuickPlace])

  // Determine if existing aircraft is selected (and we're NOT creating new)
  const hasExistingAircraft = !!selectedAircraft && !isCreatingNewAircraft

  // Can we quick-place?
  const stepComplete = isStepComplete(0)

  // Show autocomplete results: when typing, no exact match yet, and not already in create mode
  const showAutocomplete = !noTailNumber && tailSearchQuery.length >= 1 && !exactMatch && !hasExistingAircraft && !isCreatingNewAircraft

  return (
    <div className="space-y-6">
      {/* Tail Number Search */}
      {!noTailNumber && (
        <div className="space-y-0">
          <Label className="mb-2 block">Tail Number</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Enter tail number (e.g., N12345)"
              value={tailSearchQuery}
              onChange={(e) => handleTailSearchChange(e.target.value)}
              className={cn(
                'pl-10 font-mono uppercase',
                showAutocomplete && 'rounded-b-none border-b-0'
              )}
              maxLength={20}
              autoFocus
            />
            {isLoadingAircraft && tailSearchQuery.length > 0 && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Inline autocomplete results */}
          {showAutocomplete && (
            <div className="border border-t-0 rounded-b-lg overflow-hidden">
              {tailSearchResults.map((aircraft) => (
                <button
                  key={aircraft.id}
                  type="button"
                  onClick={() => handleExistingAircraftSelect(aircraft.id)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Plane className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium font-mono">{aircraft.tail_number}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {getAircraftDisplayName(aircraft)}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
              {/* "Add New Aircraft" — always shown at bottom of results */}
              <button
                type="button"
                onClick={() => toggleCreateNewAircraft(true)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left border-t hover:bg-muted/50 transition-colors text-primary"
              >
                <Plus className="h-4 w-4 shrink-0" />
                <span className="font-medium text-sm">Add New Aircraft</span>
                {tailSearchQuery && (
                  <span className="text-xs text-muted-foreground ml-auto font-mono">{tailSearchQuery}</span>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Existing Aircraft Info Card — shown when an existing aircraft is matched */}
      {hasExistingAircraft && selectedAircraft && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Plane className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="font-semibold font-mono">{selectedAircraft.tail_number}</div>
              <div className="text-sm text-muted-foreground">
                {getAircraftDisplayName(selectedAircraft)}
              </div>
            </div>
            <Badge variant="secondary">{selectedAircraft.status}</Badge>
          </div>
        </div>
      )}

      {/* New Aircraft section — shown when user clicked "Add New Aircraft" or checked noTailNumber */}
      {isCreatingNewAircraft && (
        <div className="space-y-4">
          {/* No tail number checkbox */}
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="no-tail"
                checked={noTailNumber}
                onCheckedChange={(checked) => handleNoTailToggle(checked === true)}
              />
              <label
                htmlFor="no-tail"
                className="text-sm text-muted-foreground cursor-pointer"
              >
                I don't have the tail number
              </label>
            </div>
            {/* Validation hint: show when type is selected but no tail provided */}
            {selectedAircraftTypeId && !noTailNumber && !tailNumber.trim() && (
              <p className="text-xs text-amber-600 dark:text-amber-400 pl-6">
                Enter a tail number above or check the box to continue
              </p>
            )}
          </div>

          {/* Aircraft Type Selection */}
          <div className="space-y-2">
            <Label>Aircraft Type *</Label>
            <Popover open={typeSearchOpen} onOpenChange={setTypeSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={typeSearchOpen}
                  className={cn(
                    'w-full justify-between font-normal',
                    !selectedType && 'text-muted-foreground'
                  )}
                >
                  <div className="flex items-center gap-2 truncate">
                    <Plane className="w-4 h-4 shrink-0 opacity-50" />
                    <span className="truncate">
                      {selectedType
                        ? getTypeDisplayName(selectedType)
                        : 'Search aircraft type...'}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[500px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search by manufacturer, model, or ICAO code..."
                    value={typeSearchQuery}
                    onValueChange={setTypeSearchQuery}
                  />
                  <CommandList>
                    {isLoadingTypes && (
                      <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading aircraft types...
                      </div>
                    )}

                    {!isLoadingTypes && filteredTypes.length === 0 && (
                      <CommandEmpty>
                        No aircraft types found for "{typeSearchQuery}"
                      </CommandEmpty>
                    )}

                    {!isLoadingTypes && filteredTypes.length > 0 && (
                      <CommandGroup heading="Aircraft Types">
                        {filteredTypes.map((type) => (
                          <CommandItem
                            key={type.id}
                            value={type.id}
                            onSelect={() => handleTypeSelect(type.id)}
                            className="flex items-center justify-between gap-2 cursor-pointer"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Plane className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium truncate">
                                  {getTypeDisplayName(type)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {type.icao_designator && `ICAO: ${type.icao_designator}`}
                                  {type.icao_designator && type.wingspan && ' · '}
                                  {type.wingspan && `Wingspan: ${type.wingspan}ft`}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {type.category && (
                                <Badge variant="secondary" className="text-xs">
                                  {type.category}
                                </Badge>
                              )}
                              {selectedAircraftTypeId === type.id && (
                                <Check className="h-4 w-4" />
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Temp tail number warning */}
          {noTailNumber && selectedType && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm">
              <span className="font-medium text-amber-800 dark:text-amber-200">
                A temporary tail number will be generated.
              </span>
              <span className="text-amber-700 dark:text-amber-300 ml-1">
                Update it later from the aircraft details.
              </span>
            </div>
          )}

          {/* Summary card */}
          {selectedType && (tailSearchQuery || noTailNumber) && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Plane className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">
                    {noTailNumber ? (
                      <span className="text-muted-foreground italic">Temp tail (auto-generated)</span>
                    ) : (
                      <span className="font-mono">{tailSearchQuery}</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedAircraftTypeName}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Place button - shown when step is complete and quick-place is available (ramp mode only) */}
      {stepComplete && onQuickPlace && (
        <div className="pt-2 border-t space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={handleQuickPlace}
          >
            <MapPin className="w-4 h-4" />
            Place on Map
            <span className="text-xs text-muted-foreground ml-1">(skip client & contract)</span>
          </Button>
        </div>
      )}
    </div>
  )
}

export default AircraftStep
